import { divIcon, icon } from 'leaflet';
import { isNil } from 'lodash';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { MapSatelliteContext, Marker, Popup, Tooltip } from 'components/Map';
import { StyledText } from 'components/Map/Map.style';
import { MARKER_BLUE } from 'utils/constants';

import { getTurbineColor } from '../helpers';

import { windTurbineIcon } from './windTurbineIcon';

const iconDefaults = {
  iconSize: [30, 30],
  shadowSize: [50, 64],
  iconAnchor: [15, 30],
  shadowAnchor: [4, 62],
  popupAnchor: [-3, -76],
};

const createMarkerIcon = (iconUrl, iconProps, selected) =>
  icon({ iconUrl, ...iconDefaults, ...iconProps, className: selected ? 'active' : '' });

const createTurbineSvgIcon = (html, iconProps = {}, selected) =>
  divIcon({
    html,
    className: selected ? 'active turbine-svg-icon' : 'turbine-svg-icon',
    ...iconDefaults,
    popupAnchor: [0, -30],
    ...iconProps,
  });

// TODO: TS-ify this
function ClickMarkerComponent(props) {
  const {
    mapRef,
    content,
    label = '',
    icon,
    iconProps,
    isIconTransparent,
    selected,
    openPopup = false,
    setSelectedTurbine,
    index,
    position,
    isDraggable,
    setHasDraggingStarted,
    // TODO: Make this optional since it's not used eg. in the site details map view
    setTurbineToUpdate,
    name,
    rowIndex,
    assetTypeName,
    inspectionData,
    isWorkOrderTasksList,
    item,
  } = props;
  const theme = useTheme();
  const isSatelliteView = useContext(MapSatelliteContext);
  const { maxSeverity } = inspectionData ?? {};

  // Soft white glow only for severity-colored icons on satellite imagery
  const withSoftGlow = isSatelliteView && !isNil(maxSeverity);

  /*
    Leaflet re-applies the icon whenever this prop changes identity, which rebuilds the
    marker element. Building it once per appearance keeps re-renders off the main thread.
  */
  // isDraggable is true only when preview mode true
  const markerIcon = useMemo(
    () =>
      isDraggable
        ? createMarkerIcon(icon || MARKER_BLUE, iconProps, selected)
        : isWorkOrderTasksList
          ? createTurbineSvgIcon(
              windTurbineIcon(
                item.isLinkedWithTask
                  ? theme.teal
                  : isSatelliteView
                    ? theme.mapControlsIconColor
                    : theme.mapPicturePinStroke,
                item.isTasksCompleted,
                theme.green
              ),
              iconProps,
              selected
            )
          : createTurbineSvgIcon(
              windTurbineIcon(
                getTurbineColor(theme, maxSeverity, 5, isSatelliteView),
                undefined,
                undefined,
                withSoftGlow ? theme.mapControlsIconColor : undefined
              ),
              iconProps,
              selected
            ),
    [
      icon,
      iconProps,
      isDraggable,
      isSatelliteView,
      isWorkOrderTasksList,
      item,
      maxSeverity,
      selected,
      theme,
      withSoftGlow,
    ]
  );

  const [popupRefReady, setPopupRefReady] = useState(false);
  const [markerRefReady, setMarkerRefReady] = useState(false);

  const handlePopupRef = useCallback((popup) => {
    setPopupRefReady(Boolean(popup));
  }, []);

  const { lat, lng } = position;
  /*
    A new position array makes the cluster group drop and re-add the Marker, so every
    Turbine would be re-clustered on each render.
  */
  const markerPosition = useMemo(() => [lat, lng], [lat, lng]);

  const closeTimeout = useRef();
  const markerRef = useRef();

  const handleMarkerRef = useCallback((marker) => {
    markerRef.current = marker;
    setMarkerRefReady(Boolean(marker));
  }, []);

  /*
    Selecting a Turbine opens its Popup. This has to go through the Marker: Leaflet only
    sets the Popup's latlng in the Marker's open path, and opening the Popup itself skips
    that and leaves Leaflet projecting an undefined position.
  */
  useEffect(() => {
    if (popupRefReady && markerRefReady && openPopup) {
      markerRef.current?.openPopup();
    }
  }, [openPopup, popupRefReady, markerRefReady]);

  const handleMouseOver = useCallback(
    (e) => {
      // Leaflet already closes the previously open Popup on openPopup(). Only close
      // another Marker's Popup here; re-hovering the same one would cycle close/open.
      if (!e.target.isPopupOpen?.()) {
        mapRef?.closePopup();
      }

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
      e.target.openPopup();
    },
    [mapRef]
  );

  // Some delay, so that user can interact with pop up content
  const handleMouseOut = useCallback((e) => {
    closeTimeout.current = setTimeout(() => {
      e.target.closePopup();
    }, 200);
  }, []);

  const eventHandlers = useMemo(
    () => ({
      click: () => {
        // When pin is selected from map, also select it in the list
        setSelectedTurbine(index);
      },
      dragstart: () => {
        // before setting new values, need to set selected turbine
        setSelectedTurbine(index);
        setHasDraggingStarted(true);
      },
      dragend: (e) => {
        setTurbineToUpdate({
          name: name,
          lat: e.target._latlng.lat,
          lng: e.target._latlng.lng,
          rowIndex,
          assetTypeName,
        });
      },
      ...(isDraggable ? {} : { mouseover: handleMouseOver, mouseout: handleMouseOut }),
    }),
    [
      assetTypeName,
      handleMouseOut,
      handleMouseOver,
      index,
      isDraggable,
      name,
      rowIndex,
      setHasDraggingStarted,
      setSelectedTurbine,
      setTurbineToUpdate,
    ]
  );

  useEffect(() => {
    if (isDraggable) {
      return;
    }

    const marker = markerRef.current;
    if (!marker) {
      return;
    }

    let popupDom = null;

    const handleMouseEnter = () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
        closeTimeout.current = null;
      }
    };

    const handleMouseLeave = () => {
      marker.closePopup();
    };

    const handlePopupOpen = () => {
      popupDom = marker.getPopup()?.getElement();
      if (!popupDom) {
        return;
      }

      popupDom.addEventListener('mouseenter', handleMouseEnter);
      popupDom.addEventListener('mouseleave', handleMouseLeave);
    };

    const handlePopupClose = () => {
      if (popupDom) {
        popupDom.removeEventListener('mouseenter', handleMouseEnter);
        popupDom.removeEventListener('mouseleave', handleMouseLeave);
        popupDom = null;
      }
    };

    marker.on('popupopen', handlePopupOpen);
    marker.on('popupclose', handlePopupClose);

    return () => {
      marker.off('popupopen', handlePopupOpen);
      marker.off('popupclose', handlePopupClose);

      if (popupDom) {
        popupDom.removeEventListener('mouseenter', handleMouseEnter);
        popupDom.removeEventListener('mouseleave', handleMouseLeave);
      }

      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, [isDraggable]);

  return (
    <Marker
      position={markerPosition}
      icon={markerIcon}
      opacity={isIconTransparent ? 0 : 1}
      draggable={isDraggable}
      ref={handleMarkerRef}
      eventHandlers={eventHandlers}
    >
      {label && (
        <Tooltip permanent interactive direction="right" className="custom-tooltip">
          <StyledText $isSatelliteView={isSatelliteView}>{label}</StyledText>
        </Tooltip>
      )}
      {content && <Popup ref={handlePopupRef}>{content}</Popup>}
    </Marker>
  );
}

/*
  A Site can hold hundreds of Turbines across two layers, and every render hands Leaflet
  fresh position, icon and handler objects for each of them. Skipping renders whose props
  are unchanged is what keeps tab switches from blocking the main thread.
*/
export const ClickMarker = memo(ClickMarkerComponent);
